import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardDesign } from './gift-design.service';

export const DEFAULT_DESIGN: Required<CardDesign> = {
  theme: 'midnight', background: '#1e293b', background2: '#334155', accent: '#f59e0b', text: '#ffffff',
  pattern: 'waves', headline: 'Gift Card', show_logo: true, show_amount: true, corner: 'rounded',
};

const PATTERNS: Record<string, string> = {
  none: 'none',
  waves: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='40'%3E%3Cpath d='M0 20 Q30 0 60 20 T120 20' fill='none' stroke='white' stroke-opacity='.12' stroke-width='2'/%3E%3C/svg%3E")`,
  dots: `radial-gradient(rgba(255,255,255,.16) 1.5px, transparent 1.6px) 0 0/16px 16px`,
  stripes: `repeating-linear-gradient(135deg, rgba(0,0,0,.05) 0 10px, transparent 10px 20px)`,
  grid: `linear-gradient(rgba(255,255,255,.08) 1px, transparent 1px) 0 0/22px 22px, linear-gradient(90deg, rgba(255,255,255,.08) 1px, transparent 1px) 0 0/22px 22px`,
  confetti: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='90' height='90'%3E%3Cg fill='white' fill-opacity='.18'%3E%3Crect x='8' y='12' width='8' height='3' transform='rotate(25 12 13)'/%3E%3Crect x='52' y='30' width='8' height='3' transform='rotate(-30 56 31)'/%3E%3Ccircle cx='30' cy='62' r='2.5'/%3E%3Crect x='70' y='70' width='7' height='3' transform='rotate(60 73 71)'/%3E%3Ccircle cx='78' cy='14' r='2'/%3E%3C/g%3E%3C/svg%3E")`,
};

/** A gift card drawn from the design the store made in the business console (same look as the console preview). */
@Component({
  selector: 'app-gift-card-face',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="face" [class.square]="d.corner === 'square'" [style.color]="d.text" [style.background]="bg" role="img"
         [attr.aria-label]="(headline || d.headline) + ' from ' + storeName + (amount !== null && d.show_amount ? ', ' + amountText : '')">
      <img class="photo" *ngIf="image" [src]="image" alt="" />
      <div class="top">
        <span class="logo" *ngIf="d.show_logo" [style.background]="d.accent" [style.color]="d.background">
          <img *ngIf="logo" [src]="logo" alt="" /><ng-container *ngIf="!logo">{{ initials }}</ng-container>
        </span>
        <span class="store">{{ storeName }}</span>
      </div>
      <div class="mid">
        <span class="headline">{{ headline || d.headline }}</span>
        <span class="amount" *ngIf="d.show_amount && amount !== null" [style.color]="d.accent">{{ amountText }}</span>
      </div>
      <div class="bottom">
        <span class="num">{{ number ? '•••• ' + number.slice(-4) : '•••• ••••' }}</span>
        <span class="chip" [style.background]="d.accent"></span>
      </div>
    </div>`,
  styles: [`
    :host { display: block; container-type: inline-size; }
    .face { position: relative; overflow: hidden; aspect-ratio: 1.586; border-radius: 16px; border-radius: 6cqw; padding: 16px 18px; padding: 6cqw 7cqw; box-sizing: border-box;
      display: flex; flex-direction: column; justify-content: space-between; box-shadow: 0 10px 30px rgba(15, 23, 42, .22), inset 0 0 0 1px rgba(255,255,255,.08);
      font-family: inherit; }
    .face.square { border-radius: 4px; border-radius: 1.5cqw; }
    .photo { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: .35; mix-blend-mode: luminosity; }
    .top, .mid, .bottom { position: relative; display: flex; align-items: center; gap: 3cqw; }
    .logo { display: grid; place-items: center; width: 30px; height: 30px; width: 11cqw; height: 11cqw; border-radius: 50%; font-weight: 700; font-size: 12px; font-size: 4.2cqw; overflow: hidden; flex: none; }
    .logo img { width: 100%; height: 100%; object-fit: cover; }
    .store { font-weight: 600; font-size: 13px; font-size: 4.6cqw; letter-spacing: .01em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .mid { justify-content: space-between; align-items: flex-end; }
    .headline { font-size: 20px; font-size: 7.4cqw; font-weight: 700; line-height: 1.05; letter-spacing: -.01em; max-width: 62%; }
    .amount { font-size: 24px; font-size: 9cqw; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1; }
    .bottom { justify-content: space-between; }
    .num { font-size: 11px; font-size: 3.8cqw; letter-spacing: .14em; opacity: .85; font-variant-numeric: tabular-nums; }
    .chip { width: 28px; height: 20px; width: 10cqw; height: 7.5cqw; border-radius: 4px; opacity: .9; }`],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GiftCardFaceComponent {
  @Input() set design(v: CardDesign | null | undefined) { this.d = { ...DEFAULT_DESIGN, ...(v || {}) }; }
  @Input() storeName = '';
  @Input() amount: number | null = null;
  @Input() number = '';
  @Input() headline = '';
  @Input() image: string | null = null;
  @Input() logo: string | null = null;

  d: Required<CardDesign> = DEFAULT_DESIGN;

  get bg(): string {
    const grad = `linear-gradient(135deg, ${this.d.background} 0%, ${this.d.background2 || this.d.background} 100%)`;
    const pat = PATTERNS[this.d.pattern] || 'none';
    return pat === 'none' ? grad : `${pat}, ${grad}`;
  }

  get initials(): string {
    return (this.storeName || 'G').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  }

  get amountText(): string {
    const a = this.amount ?? 0;
    return '$' + a.toLocaleString('en-US', { minimumFractionDigits: a % 1 ? 2 : 0, maximumFractionDigits: 2 });
  }
}
